import React, { useEffect, useState, useRef } from "react";
import { KBD } from "../utils/Keybindings";
import { listen } from "../hooks/Listener";
import { drawPicker } from "../popovers/Picker";
import Space from "../ui/Space";
import Label from "../ui/Label";
import Divider from "../ui/Divider";
import Input from "../ui/Input";
import { VALIDATE } from "../utils/Validators";
import Checkbox from "../ui/Checkbox";
import Status from "../ui/Status";
import { InfoIcon } from "../utils/Icons";
import { addISBNDashes } from "../utils/Utils";
import { pubCall } from "../utils/PubSub";
import { showError, showMessage } from "../ui/Message";


export const employee_permissions = {
    "adjust_onhand": false,
    "ap": false,
    "class_editing": false,
    "database_repair_utility": false,
    "edit_onhand_no_tracking": false,
    "frequent_buyer_points": false,
    "gl_book_keeping": false,
    "gl_confidential_info": false,
    "inventory_stats": false,
    "master": false,
    "ordering_processing": false,
    "pos_edit_delete": false,
    "pos_new_edit_delete": false,
    "pos_sales_tape_date_totals": false,
    "pos_subtotaling": false,
    "pos_totaling": false,
    "pos_z_no_sale": false,
    "q_sales_entry": false,
    "setup_options": false
}


export default function Employee(props) {

    const { controller = "", data = [], route = "", callback = () => { }, signal = {}, employee = false } = props;

    useEffect(() => {
        const actions = [
            { key: KBD.ESCAPE, action: (e) => { callback() } },
        ];
        const cleanup = listen(controller, route, actions, false);
        return cleanup;
    }, []);


    const currentCodes = data.map(item => item.clerk_code);

    const refs = useRef([]);

    const [index, setIndex] = useState((employee) ? "name" : "clerk_code");

    const [tabs, setTabs] = useState([
        { key: "clerk_code", value: (employee) ? employee?.clerk_code : "" },
        { key: "name", value: (employee) ? employee?.name : "" },
    ])

    const update = (_key, value) => {
        let _t = [...tabs];
        setTabs(_t.map(tab => tab.key === _key ? { ...tab, value: value } : tab)
        );
    }

    const getVal = (_key) => {
        return tabs.find(item => item.key === _key).value
    }

    const status = {
        clerk_code: <>Enter a unique ID letter for this person.</>,
        name: <>Enter a name for this person.</>,
    }


    const editProps = (index) => ({
        onFocus: () => setIndex(index),
        ref: (e) => (refs.current[index] = e),
        edit: (employee && index === "clerk_code") ? false : true,
        controlled: true,
        autoselect: true,
        onChange: (e) => update(index, e)
    });


    const next = () => {
        if (employee) { return; }
        const currentIndex = tabs.findIndex(tab => tab.key === index);
        if (currentIndex !== -1 && currentIndex < tabs.length - 1) { return setIndex(tabs[currentIndex + 1].key); }
        return setIndex(tabs[0].key);
    };

    const prev = () => {
        if (employee) { return; }
        const currentIndex = tabs.findIndex(tab => tab.key === index);
        if (currentIndex > 0) { return setIndex(tabs[currentIndex - 1].key); }
        return setIndex(tabs[tabs.length - 1].key);
    };


    const onEnter = () => {
        switch (index) {

            case "clerk_code":

                break;
            case "name":

                break;
        }
    }


    const createEmployee = () => {
        let _employee = {
            clerk_code: getVal("clerk_code"),
            has_password: false,
            is_master: false,
            name: getVal("name"),
            permissions: employee_permissions
        }

        pubCall("setting.employee.create", { employee: _employee }, (_s, _r) => {
            if (_s) {
                callback(_r, true);
                showMessage("Employee created successfully.")
            } else {
                showError(_r)
            }
        })
    }

    const editEmployee = () => {

        let _employee = { ...employee }
        _employee.name = getVal("name");
        pubCall("setting.employee.edit", { employee: _employee }, (_s, _r) => {
            if (_s) {
                callback(_r);
                showMessage("Employee updated successfully.")
            } else {
                showError(_r)
            }
        })
    }

    const save = () => {
        if (employee) {
            editEmployee()
        } else {
            createEmployee()
        }
    }

    // Keyboard Events
    useEffect(() => {

        if (refs?.current[index]) {
            refs.current[index].focus();
        }

        const actions = [
            { key: KBD.ARROW_DOWN, action: (e) => next() },
            { key: KBD.ARROW_UP, action: (e) => prev() },
            { key: KBD.TAB, action: (e) => { next() } },
            { key: KBD.SHIFT_TAB, action: (e) => { prev() } },
            { key: KBD.ESCAPE, action: (e) => callback(false) },
            { key: KBD.ENTER, action: (e) => { onEnter() } },
            { key: KBD.PAGE_DOWN, action: (e) => { save() } }
        ];
        const cleanup = listen(controller, route, actions, (e) => {
            if (e.deltaY > 0) {
                next();
            } else {
                prev();
            }
        }, [KBD.ARROW_DOWN, KBD.ARROW_UP, KBD.TAB, KBD.SHIFT_TAB, KBD.ESCAPE, KBD.ENTER, KBD.PAGE_UP, KBD.PAGE_DOWN, KBD.CTRL_C, KBD.CTRL_V], [], [KBD.CTRL_C, KBD.CTRL_V]);
        return cleanup;
    }, [index, tabs, update]);


    const isClerkValid = (value) => {
        const alphanum = /^[a-z0-9]+$/i;
        if (alphanum.test(value) && !currentCodes.includes(value)) {
            return true;
        }
        return false;
    }



    return (<>
        <div className="shim" /><div className="shim" /><div className="shim" />
        <div style={{ "width": "320px" }}>

            <Space height={25} size={10}>
                <Label width={100}>Clerk code</Label><Divider vertical />
                <Input valFunction={(e) => isClerkValid(e)} {...editProps("clerk_code")} style={{ "textAlign": "center" }} uppercase maxLength={2} autoselect placeholder="" width={40} value={getVal("clerk_code")} />
            </Space>
            <div className="shim" />
            <Space height={25} size={10}>
                <Label width={100}>Name</Label><Divider vertical />
                <Input {...editProps("name")} maxLength={10} autoselect placeholder="" width={150} value={getVal("name")} />
            </Space>
            <div className="shim" />
            <div style={{ "textAlign": "center" }}><small className="label">Press <strong>{KBD.PAGE_DOWN.label}</strong> to save.</small></div>
            {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
            <div className="shim" /><div className="shim" />
            <Status><InfoIcon /> {status[index]}</Status>
        </div>

    </>)

}